import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { storeReflection } from '../lib/aiReflections';
import { generateJourneyPDF } from '../components/journey/JourneyPdfGenerator';
import JourneyDetailDialog from '../components/journey/JourneyDetailDialog';
import JourneyUsecaseDialog from '../components/journey/JourneyUsecaseDialog';
import { 
  FileText, Download, Share2, Trophy, Heart, Briefcase, 
  Calendar, CheckCircle, Star, Sparkles, Send, Printer
} from 'lucide-react';

export default function JourneyReportPage() {
  const { user, axiosAuth } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareNote, setShareNote] = useState('');
  const [shareNoteOriginal, setShareNoteOriginal] = useState('');
  const [viewRefined, setViewRefined] = useState(user.role === 'court_officer' || user.role === 'referral_source');
  const [shareOpen, setShareOpen] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [usecaseModal, setUsecaseModal] = useState(null);
  const [usecaseLoading, setUsecaseLoading] = useState(false);
  const [usecaseContent, setUsecaseContent] = useState('');
  const printRef = useRef();
  const api = axiosAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get(`/journey/report/${user.id}`).then(r => {
      setReport(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user.id]);

  const handleShare = async () => {
    try {
      const res = await api.post(`/journey/share/${user.id}`, null, { params: { note: shareNote } });
      if (res.data.shared) {
        toast.success(res.data.message);
        if (shareNote?.trim()) {
          await storeReflection(api, {
            original_text: shareNoteOriginal || shareNote,
            refined_text: shareNote,
            context: 'journey_share',
            task_name: 'Journey Share Note',
            task_description: 'Note shared with peer support team',
            category: 'growth',
            source: 'journey_report'
          });
        }
        setShareOpen(false);
        setShareNote('');
        setShareNoteOriginal('');
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Failed to share journey');
    }
  };

  const openUsecase = async (purpose) => {
    try {
      setUsecaseModal(purpose);
      setUsecaseLoading(true);
      setUsecaseContent('');
      const res = await api.post(`/journey/report/usecase/${user.id}`, { purpose });
      setUsecaseContent(res.data.content || '');
    } catch {
      toast.error('Unable to generate document');
    } finally {
      setUsecaseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">Unable to load journey report.</p>
      </div>
    );
  }

  const milestoneStories = Object.values(report.journey_summary.milestones_by_category || {})
    .flatMap(cat => cat.milestones || [])
    .filter(m => m.story || m.story_refined || m.story_original);
  const engagement = report.program_engagement || {};
  const evidenceSteps = (engagement.roadmap_steps || []).filter(step => (step.evidence_doc_ids || []).length > 0);

  return (
    <div className="space-y-6" data-testid="journey-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>My Journey Report</h1>
          <p className="text-muted-foreground text-sm mt-1">Share your progress with employers, court, or your support team</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={shareOpen} onOpenChange={setShareOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full gap-2" data-testid="share-journey-button">
                <Share2 size={16} /> Share with Team
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle>Share Your Journey</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  Your journey progress will be shared with your peer support specialist.
                </p>
                <DictationInput
                  value={shareNote}
                  onChange={(cleaned, original) => { setShareNote(cleaned); setShareNoteOriginal(original); }}
                  placeholder="Add a personal note (optional)..."
                  rows={3}
                  promptContext={{
                    context: 'journey_share',
                    taskName: 'Journey Share Note',
                    taskDescription: 'Summarize how you want your team to understand your progress',
                    category: 'growth'
                  }}
                  data-testid="journey-share-note"
                />
                <Button onClick={handleShare} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2">
                  <Send size={16} /> Share Journey
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={() => generateJourneyPDF(report)} variant="outline" className="rounded-full gap-2" data-testid="download-pdf-button">
            <Download size={16} /> Download PDF
          </Button>
          <Button onClick={() => window.print()} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="print-report-button">
            <Printer size={16} /> Print Report
          </Button>
        </div>
      </div>

      {/* Printable Report Content */}
      <div ref={printRef} className="space-y-4 print:space-y-3">
        {/* Header */}
        <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-teal-50 to-sky-50 print:shadow-none print:border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-teal-600 flex items-center justify-center print:bg-teal-100">
                <Sparkles className="w-8 h-8 text-white print:text-teal-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-teal-900">{report.participant.name}</h2>
                <p className="text-teal-700">Journey Progress Report</p>
                <p className="text-sm text-teal-600">{report.participant.days_in_program} days in program</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Journey Summary */}
        <Card className="border-0 shadow-sm rounded-2xl print:shadow-none print:border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" /> Journey Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center mb-4">
              <button type="button" className="p-3 rounded-xl bg-violet-50 hover:bg-violet-100 transition" onClick={() => setDetailModal({ type: 'milestones' })} data-testid="journey-summary-milestones">
                <p className="text-2xl font-bold text-violet-700">{report.journey_summary.total_milestones}</p>
                <p className="text-xs text-violet-600">Life Milestones</p>
              </button>
              <button type="button" className="p-3 rounded-xl bg-teal-50 hover:bg-teal-100 transition" onClick={() => setDetailModal({ type: 'progress' })} data-testid="journey-summary-progress">
                <p className="text-2xl font-bold text-teal-700">{report.journey_summary.progress_percent}%</p>
                <p className="text-xs text-teal-600">Journey Complete</p>
              </button>
              <button type="button" className="p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition" onClick={() => setDetailModal({ type: 'achievements' })} data-testid="journey-summary-achievements">
                <p className="text-2xl font-bold text-amber-700">{report.achievements.length}</p>
                <p className="text-xs text-amber-600">Achievements</p>
              </button>
            </div>
            
            {Object.entries(report.journey_summary.milestones_by_category).length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium">Milestones Achieved:</p>
                {Object.entries(report.journey_summary.milestones_by_category).map(([cat, data]) => (
                  <div key={cat} className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase">{data.name}</p>
                    <div className="flex flex-wrap gap-2">
                      {data.milestones.map((m, i) => (
                        <Badge key={`item-${i}`} variant="secondary" className="bg-emerald-50 text-emerald-700">
                          <CheckCircle size={10} className="mr-1" /> {m.title}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {milestoneStories.length > 0 && (
          <Card className="border-0 shadow-sm rounded-2xl print:shadow-none print:border" data-testid="journey-reflections-card">
            <CardHeader className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-teal-600" /> Milestone Reflections
              </CardTitle>
              <div className="flex gap-2">
                <button type="button" onClick={() => setViewRefined(false)} className={`text-xs px-3 py-1 rounded-full border ${!viewRefined ? 'bg-teal-600 text-white border-teal-600' : 'text-teal-700 border-teal-200'}`} data-testid="journey-reflections-original-toggle">Original</button>
                <button type="button" onClick={() => setViewRefined(true)} className={`text-xs px-3 py-1 rounded-full border ${viewRefined ? 'bg-teal-600 text-white border-teal-600' : 'text-teal-700 border-teal-200'}`} data-testid="journey-reflections-refined-toggle">Refined for Authorities</button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {milestoneStories.map((m, i) => (
                  <div key={`${m.title}-${i}`} className="p-3 rounded-xl bg-muted/30" data-testid={`journey-reflection-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium">{m.title}</p>
                      <span className="text-xs text-muted-foreground">{m.date}</span>
                    </div>
                    <p className="text-sm text-slate-700">
                      {viewRefined ? (m.story_refined || m.story || m.story_original) : (m.story_original || m.story || m.story_refined)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Program Engagement */}
        <Card className="border-0 shadow-sm rounded-2xl print:shadow-none print:border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Heart className="w-4 h-4 text-rose-500" /> Program Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button type="button" className="p-3 rounded-xl bg-muted/50 text-center hover:bg-muted/70 transition" onClick={() => setDetailModal({ type: 'checkins' })} data-testid="program-engagement-checkins">
                <p className="text-lg font-bold">{report.program_engagement.total_checkins}</p>
                <p className="text-xs text-muted-foreground">Check-ins</p>
              </button>
              <button type="button" className="p-3 rounded-xl bg-emerald-50 text-center hover:bg-emerald-100 transition" onClick={() => setDetailModal({ type: 'sober' })} data-testid="program-engagement-sober">
                <p className="text-lg font-bold text-emerald-700">{report.program_engagement.sober_days_reported}</p>
                <p className="text-xs text-emerald-600">Sober Days</p>
              </button>
              <button type="button" className="p-3 rounded-xl bg-sky-50 text-center hover:bg-sky-100 transition" onClick={() => setDetailModal({ type: 'workdays' })} data-testid="program-engagement-workdays">
                <p className="text-lg font-bold text-sky-700">{report.program_engagement.work_days_reported}</p>
                <p className="text-xs text-sky-600">Work Days</p>
              </button>
              <button type="button" className="p-3 rounded-xl bg-amber-50 text-center hover:bg-amber-100 transition" onClick={() => setDetailModal({ type: 'roadmap' })} data-testid="program-engagement-roadmap">
                <p className="text-lg font-bold text-amber-700">{report.program_engagement.roadmap_milestones_completed}</p>
                <p className="text-xs text-amber-600">Roadmap Steps</p>
              </button>
            </div>
          </CardContent>
        </Card>

        {evidenceSteps.length > 0 && (
          <Card className="border-0 shadow-sm rounded-2xl print:shadow-none print:border" data-testid="journey-evidence">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-600" /> Document Evidence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {evidenceSteps.map(step => (
                <div key={step.milestone_key} className="p-3 rounded-xl bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold">{step.title}</p>
                      <p className="text-xs text-muted-foreground">{step.completed_date || 'Date pending approval'}</p>
                    </div>
                    <Badge variant="secondary" className={step.completed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}>{step.completed ? 'Completed' : 'Pending'}</Badge>
                  </div>
                  {step.evidence_notes && <p className="text-xs text-slate-600 mb-2">Notes: {step.evidence_notes}</p>}
                  <div className="flex flex-wrap gap-2">
                    {step.evidence_doc_ids.map(docId => (
                      <Button key={docId} size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`${backendUrl}/api/documents/${docId}/download`, '_blank')} data-testid={`journey-doc-${docId}`}>View Document</Button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Achievements */}
        {report.achievements.length > 0 && (
          <Card className="border-0 shadow-sm rounded-2xl print:shadow-none print:border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> Achievements Earned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {report.achievements.map((a, i) => (
                  <button type="button" key={`item-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100" onClick={() => setDetailModal({ type: 'achievement', achievement: a })} data-testid={`journey-achievement-${i}`}>
                    <Trophy size={14} className="text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">{a.title}</span>
                    <span className="text-xs text-amber-600">{a.date}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground pt-4 print:pt-2">
          <p>Generated on {new Date(report.generated_at).toLocaleDateString()} - Purpose Driven Peer Support Platform</p>
          <p className="mt-1">This report reflects self-reported progress and program participation.</p>
        </div>
      </div>

      {/* Use Cases */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-violet-50 to-pink-50 print:hidden">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-3">Use This Report For:</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <button type="button" className="p-3 rounded-xl bg-white/50 hover:bg-white transition text-left" onClick={() => openUsecase('job_interview')} data-testid="journey-usecase-job">
              <Briefcase className="w-5 h-5 text-violet-600 mb-2" />
              <p className="text-sm font-medium">Job Interviews</p>
              <p className="text-xs text-muted-foreground">Show employers your commitment to growth</p>
            </button>
            <button type="button" className="p-3 rounded-xl bg-white/50 hover:bg-white transition text-left" onClick={() => openUsecase('court_compliance')} data-testid="journey-usecase-court">
              <FileText className="w-5 h-5 text-violet-600 mb-2" />
              <p className="text-sm font-medium">Court Compliance</p>
              <p className="text-xs text-muted-foreground">Document your program participation</p>
            </button>
            <button type="button" className="p-3 rounded-xl bg-white/50 hover:bg-white transition text-left" onClick={() => openUsecase('personal_motivation')} data-testid="journey-usecase-motivation">
              <Heart className="w-5 h-5 text-violet-600 mb-2" />
              <p className="text-sm font-medium">Personal Motivation</p>
              <p className="text-xs text-muted-foreground">Celebrate how far you've come</p>
            </button>
          </div>
        </CardContent>
      </Card>

      <JourneyDetailDialog detailModal={detailModal} setDetailModal={setDetailModal} report={report} engagement={engagement} />
      <JourneyUsecaseDialog usecaseModal={usecaseModal} setUsecaseModal={setUsecaseModal} usecaseLoading={usecaseLoading} usecaseContent={usecaseContent} />
    </div>
  );
}
